import React from 'react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import { FONT_FAMILY_ROBOTO } from 'shared/constants';
import Icon from '@ff/ui/Icon';
import { Props } from './props-schema';
import CleanedHtml from 'shared/components/CleanedHtml';
import AddToCalender from './AddToCalendar';
import ShareIcon from './ShareIcon';

const newTabProps = {
  target: "_blank",
  rel: "nofollow noreferrer"
}

// Returns function which opens URL
const getOpenUrl = (url, openInNewTab) => () => {
  if (openInNewTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
}

export default function EventHeroImage(props: Props) {
  return (
    <FillViewportContainer>
      {/* Background images */}
      <DesktopBackgroundImage backgroundImage={`url(${props.background?.desktopImage})`} />
      <MobileBackgroundImage backgroundImage={`url(${props.background?.mobileImage})`} />

      {/* Custom header */}
      {
        props.topContent?.showHomeHeader &&
          <CustomHeader>
            <HomeButton
              href={props.topContent?.homeHeaderTarget}
              {...(props.topContent?.homeHeaderOpenInNewTab && newTabProps)}
            >
              {props.topContent?.homeHeaderText}
            </HomeButton>
            <Icon
              name="ff-icon-logo"
              onClick={getOpenUrl(props.topContent?.homeHeaderTarget, props.topContent?.homeHeaderOpenInNewTab)}
            />
          </CustomHeader>
      }

      {/* Content */}
      <TopContent paddingX="16px" hasHeader={!!props.topContent?.headerImage}>
        {
          props.topContent?.headerImage &&
            <HeaderImageWrapper>
              <HeaderImage height="102px" src={props.topContent?.headerImage} />
            </HeaderImageWrapper>
        }
        <DesktopOnly>
          {
            props.topContent?.title &&
              <TitleTextWrapper>
                <CleanedHtml>
                  {props.topContent?.title}
                </CleanedHtml>
              </TitleTextWrapper>
          }
          {
            props.topContent?.subtitle &&
              <SubtitleTextWrapper>
                <CleanedHtml>
                  {props.topContent?.subtitle}
                </CleanedHtml>
              </SubtitleTextWrapper>
          }
        </DesktopOnly>
        <MobileOnly>
          {
            props.topContent?.mobileTitle &&
              <TitleTextWrapper>
                <CleanedHtml>
                  {props.topContent?.mobileTitle}
                </CleanedHtml>
              </TitleTextWrapper>
          }

          {
            props.topContent?.mobileSubtitle &&
              <SubtitleTextWrapper>
                <CleanedHtml>
                  {props.topContent?.mobileSubtitle}
                </CleanedHtml>
              </SubtitleTextWrapper>
          }
        </MobileOnly>
        <TopButtonContainer>
          <TopButtonSpacer>
          {
            props.topContent?.buttons?.map((button) => (
              <React.Fragment key={button.target}>
                {
                  props.topContent?.buttonStyle === 'UNDERLINED' && (
                    button?.addToCalendar ?
                      <AddToCalender
                        region={props.region}
                        colorTheme={props.topContent?.buttonTheme}
                        eventInformation={props?.eventInformation}
                      >
                        {button.text}
                      </AddToCalender> :
                      <BottomButtonUnderlined
                        colorTheme={props.topContent?.buttonTheme}
                        href={button.target}
                        {...(button?.openInNewTab && {newTabProps})}
                      >
                        {button.text}
                      </BottomButtonUnderlined>
                  )
                }
                {
                  props.topContent?.buttonStyle === 'TRANSPARENT' &&
                    <BottomButtonTransparent
                      colorTheme={props.topContent?.buttonTheme}
                      onClick={getOpenUrl(button.target, button.openInNewTab)}
                    >
                      {button.text}
                    </BottomButtonTransparent>
                }
              </React.Fragment>
            ))
          }
          </TopButtonSpacer>
          {
            props.topContent?.showShareIcon &&
            <TopShareIconContainer>
              <ShareIcon region={props.region} socialLinks={props.socialLinks} />
            </TopShareIconContainer>
          }
        </TopButtonContainer>
      </TopContent>
      <BottomContent>
        <BottomButtonContainer>
          {
            props.bottomContent?.buttons?.map((button) => (
              <React.Fragment key={button.target}>
                {
                  props.bottomContent?.buttonStyle === 'UNDERLINED' &&
                    <BottomButtonUnderlined
                      colorTheme={props.bottomContent?.buttonTheme}
                      href={button.target}
                      {...(button?.openInNewTab && {newTabProps})}
                    >
                      {button.text}
                    </BottomButtonUnderlined>
                }
                {
                  props.bottomContent?.buttonStyle === 'TRANSPARENT' &&
                    <BottomButtonTransparent
                      colorTheme={props.bottomContent?.buttonTheme}
                      onClick={getOpenUrl(button.target, button.openInNewTab)}
                    >
                      {button.text}
                    </BottomButtonTransparent>
                }
              </React.Fragment>
            ))
          }
        </BottomButtonContainer>
      { props.bottomContent?.showScrollIndicator && <ScrollIndicatorIcon name="ff-icon-keyboard_arrow_down" /> }
      </BottomContent>
    </FillViewportContainer>
  );
}

const FillViewportContainer = styled(Box)({
  height: '100vh',
  width: '100%',
  position: 'relative',
});

const CustomHeader = styled(Box)({
  position: 'absolute',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',

  padding: '12px 16px',

  color: 'white',
  width: '100%',
  '> i': {
    fontSize: '21px',
  },

  '@media(min-width: 600px)': {
    padding: '14px 40px',
    '> i': {
      fontSize: '30px',
    },
  }
});

const HomeButton = styled(Box)<{ href: string, target?: string, rel?: string }>({
  color: 'white',
  textDecoration: 'none',
  fontFamily: FONT_FAMILY_ROBOTO,
  fontSize: '16px',

  '@media (min-width: 600px)': {
    fontSize: '20px',
  }
});
HomeButton.defaultProps = { as: 'a' };

const DesktopBackgroundImage = styled(Box)({
  width: '100%',
  height: '100%',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  position: 'absolute',

  display: 'none',
  '@media(min-width: 600px)': {
    display: 'block',
  }
});

const MobileBackgroundImage = styled(Box)({
  width: '100%',
  height: '100%',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  position: 'absolute',
  '@media(min-width: 600px)': {
    display: 'none',
  }
});

const TopContent = styled(Box)<{ hasHeader: boolean }>(({ hasHeader }) => ({
  top: 0,
  left: 0,
  right: 0,
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: hasHeader ? '38px' : '112px',
  '@media(min-width: 600px)': {
    marginTop: hasHeader ? '120px' : '154x'
  }
}));

const BottomContent = styled(Box)({
  left: 0,
  right: 0,
  bottom: 0,
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

// Collapse on desktop
const HeaderImageWrapper = styled(Box)({
  minHeight: '100px',
  '@media(min-width: 600px)': {
    minHeight: 0,
  }
});

const HeaderImage = styled(Box)<{ src: string }>({

});
HeaderImage.defaultProps = { as: 'img' };

const TitleTextWrapper = styled(Box)({
  fontFamily: FONT_FAMILY_ROBOTO,
  textAlign: 'center',
  'p': {
    marginTop: '4px',
    marginBottom: '4px',
  }
});

const SubtitleTextWrapper = styled(Box)({
  fontFamily: FONT_FAMILY_ROBOTO,
  textAlign: 'center',
  'p': {
    marginTop: '8px',
    marginBottom: '8px',
  },
  '@media(min-width: 600px)': {
    'p': {
      marginTop: '4px',
      marginBottom: '4px',
    },
  }
});

const TopButtonContainer = styled(Box)({
  marginTop: '40px',
  display: 'flex',
  '@media(min-width: 600px)': {
    columnGap: '20px',
  }
});

const TopButtonSpacer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  columnGap: '30px',
  rowGap: '20px',
});

const TopShareIconContainer = styled(Box)({
  display: 'flex',
  marginTop: '-3px',
  '> img': {
    width: '16px'
  },
  '@media(min-width: 600px)': {
    '> img': {
      width: '23px'
    },
  }
});

const BottomButtonContainer = styled(Box)({
  bottom: '45px',
  position: 'absolute',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  columnGap: '30px',
  rowGap: '20px',

  '@media(min-width: 600px)': {
    bottom: '120px',
  }
});

export interface BottomButtonUnderlinedProps {
  href?: string;
  target?: string;
  rel?: string;
  colorTheme?: string
}
export const BottomButtonUnderlined = styled(Box)<BottomButtonUnderlinedProps>(({ colorTheme }) => ({
  color: colorTheme === 'LIGHT' ? 'white' : 'black',
  fontFamily: FONT_FAMILY_ROBOTO,
  fontSize: '16px',
  padding: '8px 16px',
  lineHeight: '24px',
  textDecoration: 'underline',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',

  '@media(min-width: 600px)': {
    padding: 0,
    lineHeight: 'normal',
  }
}));
BottomButtonUnderlined.defaultProps = { as: 'a' };

const BottomButtonTransparent = styled(Box)<{ colorTheme: string }>(({ colorTheme }) => ({
  height: '38px',
  width: '264px',
  color: colorTheme === 'LIGHT' ? 'white' : 'black',
  fontFamily: FONT_FAMILY_ROBOTO,
  fontSize: '14px',
  backgroundColor: 'transparent',
  border: `1px solid ${colorTheme === 'LIGHT' ? 'rgba(255, 255, 255, 0.65)' : 'black'}`,
  borderRadius: '4px',
  cursor: 'pointer',
}));
BottomButtonTransparent.defaultProps = { as: 'button' };

const ScrollIndicatorIcon = styled(Icon)({
  display: 'none',
  bottom: '15px',
  color: 'white',
  fontSize: '36px',
  position: 'absolute',
  textAlign: 'center',
  opacity: '0.5',
  width: '100%',

  '@media (min-width: 600px)': {
    display: 'block',
  }
});

const DesktopOnly = styled(Box)({
  display: 'none',
  '@media (min-width: 600px)': {
    display: 'block',
  }
});

const MobileOnly = styled(Box)({
  display: 'block',
  '@media (min-width: 600px)': {
    display: 'none',
  }
});
